/**
 * useSEO — hook per gestire title, meta, OG, Twitter Card e JSON-LD.
 *
 * Usa DOM manipulation diretta (compatibile con CRA + React 19).
 * JSON-LD viene iniettato/rimosso sul ciclo di vita del componente.
 *
 * Env vars:
 *   REACT_APP_SITE_URL  — URL canonico del sito (es. https://dalsud.it)
 *   REACT_APP_NOINDEX   — "true" aggiunge noindex (staging / pre-lancio)
 */

import { useEffect } from 'react';

const SITE_URL = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');
const SITE_NAME = 'Dal Sud';
const NOINDEX = process.env.REACT_APP_NOINDEX === 'true';

const DEFAULT_TITLE = 'Dal Sud — Prodotti artigianali e alimentari del Sud Italia';
const DEFAULT_DESC =
  'Scopri i migliori prodotti artigianali e alimentari del Sud Italia. ' +
  'Acquista direttamente dai produttori locali: cibo, artigianato ed esperienze autentiche.';

function setMeta(attr, attrValue, content) {
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function injectJsonLd(data) {
  removeJsonLd();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo', '');
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

function removeJsonLd() {
  document.querySelector('script[data-seo]')?.remove();
}

/**
 * @param {object} params
 * @param {string}  [params.title]         - Titolo pagina (senza "| Dal Sud")
 * @param {string}  [params.description]   - Meta description (max ~155 char)
 * @param {string}  [params.image]         - URL immagine OG (assoluto)
 * @param {string}  [params.canonicalPath] - Percorso canonico es. "/prodotti/slug"
 * @param {object}  [params.jsonLd]        - Oggetto JSON-LD schema.org
 */
export function useSEO({ title, description, image, canonicalPath, jsonLd } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESC;
    const img = image || `${SITE_URL}/og-cover.jpg`;
    const canonical = canonicalPath ? `${SITE_URL}${canonicalPath}` : null;

    document.title = fullTitle;

    if (NOINDEX) setMeta('name', 'robots', 'noindex, nofollow');

    setMeta('name', 'description', desc);

    if (canonical) setCanonical(canonical);

    // Open Graph
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:image', img);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale', 'it_IT');
    if (canonical) setMeta('property', 'og:url', canonical);

    // Twitter Card
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', desc);
    setMeta('name', 'twitter:image', img);

    if (jsonLd) injectJsonLd(jsonLd);

    return () => removeJsonLd();
  }, [title, description, image, canonicalPath]); // eslint-disable-line react-hooks/exhaustive-deps
}
